import type {
  InvoiceImportDirection,
  InvoiceImportDirectionSource,
  InvoiceImportMatchedSubjectRole,
} from '../Types/invoiceImport.types';

const DIRECTION_LABELS: Record<InvoiceImportDirection, string> = {
  purchase: 'Zakupowa',
  sales: 'Sprzedażowa',
  unknown: 'Nieznany kierunek',
};

const DIRECTION_SOURCE_LABELS: Record<InvoiceImportDirectionSource, string> = {
  persisted: 'Źródło: persisted',
  derivedRuntime: 'Źródło: derivedRuntime',
};

const MATCHED_SUBJECT_ROLE_LABELS: Record<InvoiceImportMatchedSubjectRole, string> = {
  subject1: 'Podmiot1',
  subject2: 'Podmiot2',
  none: 'Brak dopasowania',
  both: 'Podmiot1 + Podmiot2',
};

export const getInvoiceDirectionLabel = (direction: InvoiceImportDirection): string =>
  DIRECTION_LABELS[direction];

export const getInvoiceDirectionSourceLabel = (
  directionSource: InvoiceImportDirectionSource
): string => DIRECTION_SOURCE_LABELS[directionSource];

export const getMatchedSubjectRoleLabel = (
  matchedSubjectRole: InvoiceImportMatchedSubjectRole | null | undefined
): string | null => {
  if (!matchedSubjectRole || matchedSubjectRole === 'none') {
    return null;
  }

  return MATCHED_SUBJECT_ROLE_LABELS[matchedSubjectRole];
};

export interface InvoiceDirectionDecision {
  approveAllowed: boolean;
  requiresOverride: boolean;
  canOpenApproveModal: boolean;
  severity: 'success' | 'warning' | 'error';
  message: string;
}

/**
 * Prefixes mirror the backend's KsefIncomingDirectionClassifier.IsKsefImporter
 * (TravelCRM.Application/InvoiceImport/Service/KsefIncomingDirectionClassifier.cs).
 */
const KSEF_IMPORTER_TYPE_PREFIXES = [
  'ksefxmlimporter.',
  'ksefxmlimporter-',
  'ksefxmlimporter_',
  'ksefxmlimporter/',
];

const isKsefImporterType = (importerType: string | null | undefined): boolean => {
  if (!importerType) {
    return false;
  }

  const normalized = importerType.trim().toLowerCase();
  if (normalized === 'ksefxmlimporter') {
    return true;
  }

  return KSEF_IMPORTER_TYPE_PREFIXES.some((prefix) => normalized.startsWith(prefix));
};

/**
 * Direction classification (purchase/sales/unknown) only ever runs for KSeF imports
 * (backend: ApproveImportDirectionGateService.ResolveKsefDirectionAsync returns null,
 * and the approve handler's direction switch never executes, for any other importer
 * type). For non-KSeF imports the backend always reports "unknown" as an unclassified
 * placeholder (source: derivedRuntime), not a real ambiguity — requiring an override
 * for it is pure frontend friction with no backend meaning.
 */
export const getInvoiceDirectionDecision = (
  direction: InvoiceImportDirection,
  importerType: string | null | undefined
): InvoiceDirectionDecision => {
  if (direction === 'unknown' && !isKsefImporterType(importerType)) {
    return {
      approveAllowed: true,
      requiresOverride: false,
      canOpenApproveModal: true,
      severity: 'success',
      message:
        'Import spoza KSeF - kierunek nie jest klasyfikowany dla tego źródła; zatwierdzenie do ścieżki kosztowej jest dozwolone.',
    };
  }

  switch (direction) {
    case 'purchase':
      return {
        approveAllowed: true,
        requiresOverride: false,
        canOpenApproveModal: true,
        severity: 'success',
        message: 'Dokument zakupowy - zatwierdzenie do ścieżki kosztowej jest dozwolone.',
      };
    case 'sales':
      return {
        approveAllowed: false,
        requiresOverride: false,
        canOpenApproveModal: false,
        severity: 'error',
        message:
          'Dokument sprzedażowy - zatwierdzenie do ścieżki kosztowej jest zablokowane (409 directionSalesNotExpense).',
      };
    case 'unknown':
    default:
      return {
        approveAllowed: false,
        requiresOverride: true,
        canOpenApproveModal: true,
        severity: 'warning',
        message:
          'Kierunek niejednoznaczny - wymagane jawne override przed zatwierdzeniem (422 directionUnknownOverrideRequired).',
      };
  }
};
